import { useCallback } from 'react';
import {
  findAllTaskListItems,
  getFindAllTaskListItemsQueryKey,
  useUnclaimTask as useUnclaimTaskApi,
} from '@api';
import { useNotification } from '@common';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const TRANS_KEY_NOTIFICATION = 'NOTIFICATIONS.UNCLAIM_TASK';
const TASKS_QUERY_KEY = getFindAllTaskListItemsQueryKey();

type TasksListCacheType = Awaited<ReturnType<typeof findAllTaskListItems>>;

type UnclaimTaskContextType = {
  previous: TasksListCacheType | undefined;
};

type UseUnclaimTaskReturnType = {
  unclaimTask: (taskId: string) => void;
};

export const useUnclaimTask = (): UseUnclaimTaskReturnType => {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const updateCache = useCallback(
    async (taskId: string) => {
      await queryClient.cancelQueries({ queryKey: TASKS_QUERY_KEY });

      const previous =
        queryClient.getQueryData<TasksListCacheType>(TASKS_QUERY_KEY);

      queryClient.setQueryData<TasksListCacheType>(
        TASKS_QUERY_KEY,
        (current) => {
          if (!current) return current;

          return {
            ...current,
            data: current.data.map((task) =>
              task.id === taskId
                ? { ...task, claimedBy: null, claimedAt: null }
                : task
            ),
          };
        }
      );

      return { previous };
    },
    [queryClient]
  );

  const { mutate } = useUnclaimTaskApi<AxiosError, UnclaimTaskContextType>({
    mutation: {
      onMutate: async ({ taskId }) => updateCache(taskId),
      onError: (error, _variables, context) => {
        queryClient.setQueryData(TASKS_QUERY_KEY, context?.previous);

        showError({
          title: `${TRANS_KEY_NOTIFICATION}.ERROR.TITLE`,
          message: `${TRANS_KEY_NOTIFICATION}.ERROR.MESSAGE`,
          error,
        });
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      },
    },
  });

  const unclaimTask = useCallback(
    (taskId: string) => {
      mutate({ taskId });
    },
    [mutate]
  );

  return { unclaimTask };
};
